import { supabase } from '../lib/supabase'
import { photosOf, type WrapUpPhoto } from './wrapUpPhotos'

/**
 * The wrap-up: the studio's own post-show report, displayed on the finished
 * project page. Audit §4.
 *
 * **THE REPORT IS THE CONTENT. THIS IS DISPLAY.** Tom, 26 Sep: *"The PDF
 * wrap report is ALL important. Stop writing it in your own words. Just
 * figure out a way to display it in the best possible way."* The first pass
 * treated Shilpa's document as raw material — ten figures became eight
 * because four across looked neat, her closing paragraph was dropped because
 * the shape had no field for it, and the 2027 plan was rewritten shorter
 * because it read long. None of that was ours to decide. When the document
 * does not fit the page, the answer is a better display or a new field,
 * never a shorter sentence.
 *
 * **IT IS OPT-IN.** *"Some projects simply don't require anything as 'grand'
 * as a wrap up report."* Most work ends quietly. `wrapup_published` defaults
 * to false, so a project gets a report because somebody turned it on. That
 * flag is also the draft control: Shilpa wrote ATM's over several days, and
 * without it, marking the last film Delivered would have published a
 * half-written report on the day you send the client the link.
 *
 * **THE PHOTOGRAPHS ARE NOT THE GALLERY.** `photos` holds a handful of
 * frames the studio chose to carry the report — Tom, 27 Sep: *"just to make
 * the report look pretty"*. The client's actual photography deliverable is a
 * separate thing with its own contact sheet, reachable once from the
 * downloads at the end. Where each of these lands is worked out by
 * `placePhotos` in utils/wrapUpPhotos.ts; nobody chooses a position.
 *
 * Read on its own rather than threaded through `Project` — wanted on one
 * screen in one state, so every other visit should not carry it.
 */

export interface WrapUpNumber {
  /** "20", "1" — or "Multiple". Not every figure in a report is a numeral. */
  value: string
  label: string
}

export interface WrapUpQuote {
  body: string
  name: string
  role: string
}

export interface WrapUpGroup {
  title: string
  points: string[]
}

export interface WrapUpNext {
  heading: string
  /** May hold several paragraphs, separated by blank lines. */
  body: string
  groups: WrapUpGroup[]
}

export interface WrapUp {
  /** Nothing reaches the client until this is true. */
  published: boolean
  statement: string
  numbers: WrapUpNumber[]
  /** The paragraph that closes the figures. */
  closing: string
  quotes: WrapUpQuote[]
  next: WrapUpNext | null
  /** In the order staff arranged them. The page decides where they sit. */
  photos: WrapUpPhoto[]
}

export const EMPTY_WRAP_UP: WrapUp = {
  published: false,
  statement: '',
  numbers: [],
  closing: '',
  quotes: [],
  next: null,
  photos: [],
}

/** True when something is actually written — published or not. Staff side. */
export function hasWrapUpContent(wrap: WrapUp | null): wrap is WrapUp {
  if (!wrap) return false
  return (
    Boolean(wrap.statement.trim()) ||
    Boolean(wrap.closing.trim()) ||
    wrap.numbers.length > 0 ||
    wrap.quotes.length > 0 ||
    wrap.photos.length > 0 ||
    wrap.next !== null
  )
}

/**
 * True when the client should see it: published AND not empty. Unpublished
 * means nobody has said it is ready; published but empty would be a page of
 * headings with nothing under them.
 */
export function showWrapUp(wrap: WrapUp | null): wrap is WrapUp {
  return hasWrapUpContent(wrap) && wrap.published
}

/** Blank-line-separated prose → paragraphs, so a field can hold several. */
export function paragraphsOf(value: string): string[] {
  return value
    .split(/\n\s*\n/)
    .map((part) => part.trim())
    .filter(Boolean)
}

function text(value: unknown): string {
  return value === null || value === undefined ? '' : String(value)
}

function field(row: unknown, key: string): string {
  return text((row as Record<string, unknown>)?.[key]).trim()
}

/**
 * jsonb arrives as whatever was stored, so every row is checked rather than
 * trusted: a half-written entry is dropped instead of rendering a quotation
 * mark with nothing in it.
 */
function numbersOf(value: unknown): WrapUpNumber[] {
  if (!Array.isArray(value)) return []
  return value
    .map((row) => ({ value: field(row, 'value'), label: field(row, 'label') }))
    .filter((row) => row.value !== '' || row.label !== '')
}

function quotesOf(value: unknown): WrapUpQuote[] {
  if (!Array.isArray(value)) return []
  return value
    .map((row) => ({
      body: field(row, 'body'),
      name: field(row, 'name'),
      role: field(row, 'role'),
    }))
    .filter((row) => row.body !== '')
}

function groupsOf(value: unknown): WrapUpGroup[] {
  if (!Array.isArray(value)) return []
  return value
    .map((row) => {
      const points = (row as Record<string, unknown>)?.points
      return {
        title: field(row, 'title'),
        points: Array.isArray(points) ? points.map((p) => text(p).trim()).filter(Boolean) : [],
      }
    })
    .filter((row) => row.title !== '' || row.points.length > 0)
}

/** Null unless something is actually written — never an empty heading. */
function nextOf(value: unknown): WrapUpNext | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const next: WrapUpNext = {
    heading: field(value, 'heading'),
    body: field(value, 'body'),
    groups: groupsOf((value as Record<string, unknown>).groups),
  }
  if (!next.heading && !next.body && next.groups.length === 0) return null
  return next
}

function wrapOf(row: Record<string, unknown>): WrapUp {
  return {
    published: Boolean(row.wrapup_published),
    statement: text(row.wrapup_statement).trim(),
    numbers: numbersOf(row.wrapup_numbers),
    closing: text(row.wrapup_closing).trim(),
    quotes: quotesOf(row.wrapup_quotes),
    next: nextOf(row.wrapup_next),
    photos: photosOf(row.wrapup_photos),
  }
}

const COLUMNS =
  'wrapup_published, wrapup_statement, wrapup_numbers, wrapup_closing, wrapup_quotes, wrapup_next, wrapup_photos'

/** The wrap-up written for one project, or null if the read failed. */
export async function fetchWrapUp(projectId: string): Promise<WrapUp | null> {
  if (!supabase || !projectId) return null

  const { data, error } = await supabase
    .from('projects')
    .select(COLUMNS)
    .eq('id', projectId)
    .maybeSingle()

  if (error) {
    /* A refused read is not an empty wrap-up — it is no answer. The page
       keeps its ordinary completion state rather than announcing a report
       that was never written. */
    console.warn(`[supabase] wrap-up load failed: ${error.message}`)
    return null
  }
  if (!data) return null

  return wrapOf(data as Record<string, unknown>)
}

/** Where one project stands, for the Wrap-ups list. */
export interface WrapUpState {
  published: boolean
  /** Something is written, whether or not it is showing. */
  filled: boolean
}

/**
 * Every project's wrap-up state in one read, keyed by project id.
 *
 * The Wrap-ups tab has to say which finished projects still have no report,
 * which would be one query per project done the obvious way. A failed read
 * returns an empty map, so the list renders as "no wrap-up yet" everywhere
 * rather than refusing to draw — wrong in a harmless direction, and the
 * editor reads the real row when you open one.
 */
export async function fetchWrapUpIndex(): Promise<Map<string, WrapUpState>> {
  const index = new Map<string, WrapUpState>()
  if (!supabase) return index

  const { data, error } = await supabase.from('projects').select(`id, ${COLUMNS}`)

  if (error) {
    console.warn(`[supabase] wrap-up index failed: ${error.message}`)
    return index
  }

  for (const row of data ?? []) {
    const record = row as Record<string, unknown>
    const wrap = wrapOf(record)
    index.set(String(record.id), {
      published: wrap.published,
      filled: hasWrapUpContent(wrap),
    })
  }
  return index
}

/** Saves the wrap-up for one project. Staff side. */
export async function saveWrapUp(
  projectId: string,
  wrap: WrapUp,
): Promise<{ ok: boolean; error?: string }> {
  if (!supabase) return { ok: false, error: 'No database connection.' }

  const { error } = await supabase
    .from('projects')
    .update({
      wrapup_published: wrap.published,
      wrapup_statement: wrap.statement.trim() || null,
      wrapup_numbers: numbersOf(wrap.numbers),
      wrapup_closing: wrap.closing.trim() || null,
      wrapup_quotes: quotesOf(wrap.quotes),
      wrapup_next: nextOf(wrap.next),
      wrapup_photos: photosOf(wrap.photos),
    })
    .eq('id', projectId)

  if (error) return { ok: false, error: error.message }
  return { ok: true }
}
